import { useState, useContext, useRef } from 'react';
import { Modal, Form, Input, notification, Divider } from 'antd';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

import { UserResourceControllerApiFactory as userAPI } from '../../api_client/parameter_api';
import { UserSettingsProps } from './types';
import { handleError } from '../../utils/handleError';
import { getRules } from '../EditableCell/getRules';
import { ModalLeavePrompt } from '../ModalLeavePrompt';
import { ModalType } from '../../types';
import { AppContext } from '../../context/context';
import { setUserProfile } from '../../context/user/actions';
import { apiLocation } from '../../config';

const UserSettings = ({ form, modal, save, cancel }: UserSettingsProps) => {
  const [saving, setSaving] = useState<boolean>(false);
  const [leaveModal, setLeaveModal] = useState<ModalType>({
    visible: false,
    mode: 'add'
  });
  const [keyVisibale, setKeyVisibale] = useState<boolean>(false);
  const [state, dispatch] = useContext<any>(AppContext);
  const authKeyRef = useRef<Input | null>(null);
  const hiddenAuthKeyRef = useRef<Input | null>(null);

  const { userProfile } = state;

  const handleKeyCopy = async () => {
    const copyText = authKeyRef?.current ? authKeyRef?.current.input : (hiddenAuthKeyRef?.current as Input).input;
    copyText.select();
    /* For mobile devices */
    copyText.setSelectionRange(0, 99999);

    try {
      await navigator.clipboard.writeText(copyText.value);
      notification.success({ message: 'Copied' });
    } catch (err: any) {
      notification.error({ message: err });
    }
  };

  const handleOk = async () => {
    const userData = await form.validateFields();
    const { password, passwordConfirmation } = userData;
    if (password === passwordConfirmation) {
      form.setFields([{ name: 'passwordConfirmation', errors: [] }]);
      try {
        setSaving(true);
        const { data } = await userAPI(undefined, apiLocation).changePasswordForUser({ password, passwordConfirmation });
        if (userProfile.passwordChangeRequired) {
          dispatch(setUserProfile(data));
        }
        setSaving(false);
        save();
      } catch (err) {
        setSaving(false);
        handleError(err);
      }
    } else {
      form.setFields([{ name: 'passwordConfirmation', errors: ['Passwords must match'] }]);
    }
  };

  const handleCancel = () => {
    if (!userProfile.passwordChangeRequired) {
      if (form.isFieldsTouched()) {
        setLeaveModal({ ...leaveModal, visible: true });
      } else {
        cancel();
      }
    }
  };

  const handleLeaveModalConfirm = () => {
    handleLeaveModalCancel();
    cancel();
  };

  const handleLeaveModalCancel = () => setLeaveModal({ ...leaveModal, visible: false });

  const toggleKeyIcon = () => setKeyVisibale(!keyVisibale);

  return (
    <>
      <Modal
        forceRender={true}
        wrapClassName='modal'
        title='Personal settings'
        visible={modal.visible}
        okText='Save'
        okButtonProps={{ loading: saving }}
        onCancel={handleCancel}
        onOk={handleOk}
      >
        <Form layout='vertical' form={form}>
          <Form.Item label='New password' name='password' rules={getRules('password')} validateFirst={true}>
            <Input.Password size='large' placeholder='New password' />
          </Form.Item>
          <Form.Item
            label='Confirm password'
            name='passwordConfirmation'
            rules={getRules('passwordConfirmation')}
            validateFirst={true}
          >
            <Input.Password size='large' placeholder='Confirm password' />
          </Form.Item>
          <Divider />
          <div style={{ position: 'relative' }}>
            <Form.Item label='Authentication key' name='authenticationToken'>
              {keyVisibale ? (
                <Input size='large' disabled={true} ref={authKeyRef} />
              ) : (
                <Input.Password
                  size='large'
                  type='password'
                  disabled={true}
                  visibilityToggle={false}
                  ref={hiddenAuthKeyRef}
                />
              )}
            </Form.Item>
            <span className='user-settings-auth-field-btns btn-position'>
              <span onClick={handleKeyCopy}>Copy</span>
              <span className='token-eye-icon ' onClick={toggleKeyIcon}>
                {keyVisibale ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              </span>
            </span>
          </div>
        </Form>
      </Modal>
      <ModalLeavePrompt
        modal={leaveModal}
        handleConfirm={handleLeaveModalConfirm}
        handleCancel={handleLeaveModalCancel}
      />
    </>
  );
};

export default UserSettings;
